import type { Ratecard } from '@clockwise/clients/ratecards';
import { ApiError } from '@clockwise/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { AuditEntryList } from '@/components/custom/audit/audit-entry-list';
import { BackLink } from '@/components/custom/back-link';
import { ConfirmDialog } from '@/components/custom/confirm-dialog';
import {
  PillTabs,
  PillTabsContent,
  PillTabsList,
  PillTabsTrigger,
} from '@/components/custom/pill-tabs';
import { RatecardFormSheet } from '@/components/custom/ratecard-form-sheet';
import { RequirePermission } from '@/components/custom/require-permission';
import { PageShell } from '@/components/layout/page-shell';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { usePermissionGuard } from '@/hooks/use-permission-guard';
import { auditApi, ratecardsApi, serviceRolesApi } from '@/lib/api';
import { lastSearch } from '@/lib/last-search';
import { useHasPermission } from '@/lib/permissions';
import { queryKeys } from '@/lib/query-keys';

const TAB_VALUES = ['details', 'audit'] as const;
type TabValue = (typeof TAB_VALUES)[number];

const detailSearch = z.object({
  tab: z.enum(TAB_VALUES).optional(),
});

export const Route = createFileRoute('/_authenticated/setup/ratecards/$id')({
  validateSearch: detailSearch,
  component: RatecardDetailPage,
});

const AUDIT_PAGE_SIZE = 50;
const DEFAULT_TAB: TabValue = 'details';

function RatecardDetailPage() {
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const queryClient = useQueryClient();
  const { allowed: canView, fallback } = usePermissionGuard('ratecard.view', 'rate cards');
  const canEdit = useHasPermission('ratecard.edit');
  // The Audit tab reads /api/audit, gated by `audit.view`. Without it the tab
  // is hidden entirely rather than rendering a "Forbidden." panel.
  const canViewAudit = useHasPermission('audit.view');
  const requestedTab: TabValue = tab ?? DEFAULT_TAB;
  const activeTab: TabValue =
    requestedTab === 'audit' && !canViewAudit ? DEFAULT_TAB : requestedTab;

  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detail = useQuery({
    queryKey: queryKeys.ratecards.detail(id),
    queryFn: () => ratecardsApi.get(id),
    enabled: canView,
  });

  const serviceRolesQuery = useQuery({
    queryKey: queryKeys.serviceRoles.list({
      active: true,
      internal: false,
      sortField: 'name',
      sortDirection: 'asc',
      limit: 500,
    }),
    queryFn: () =>
      serviceRolesApi.list({
        active: true,
        internal: false,
        sortField: 'name',
        sortDirection: 'asc',
        limit: 500,
      }),
    staleTime: 5 * 60_000,
    // Fetch eagerly when the user can edit (not gated on the sheet being open),
    // so the editor's rate rows are ready the instant it opens rather than
    // flashing an empty "no service roles" state on first click.
    enabled: canEdit,
  });
  const serviceRoles = serviceRolesQuery.data?.data ?? [];

  const audit = useQuery({
    queryKey: queryKeys.audit.forEntity({
      entityType: 'ratecards',
      entityId: id,
      limit: AUDIT_PAGE_SIZE,
      offset: 0,
    }),
    queryFn: () =>
      auditApi.list({ entityType: 'ratecards', entityId: id, limit: AUDIT_PAGE_SIZE, offset: 0 }),
    enabled: canView && canViewAudit,
  });

  const card = detail.data?.data ?? null;

  const deleteMutation = useMutation({
    mutationFn: () => ratecardsApi.remove(id),
    onSuccess: () => {
      toast.success('Rate card deleted');
      queryClient.invalidateQueries({ queryKey: queryKeys.ratecards.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      // Back to the list as it was — minus `page`, which may be past the end now
      // that this row is gone.
      navigate({
        to: '/setup/ratecards',
        search: lastSearch('/setup/ratecards', { drop: ['page'] }),
      });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed');
    },
  });

  if (fallback) return fallback;

  return (
    <PageShell className="gap-6 overflow-y-auto">
      <div>
        <BackLink to="/setup/ratecards">Back to rate cards</BackLink>
      </div>

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          {detail.isLoading ? (
            <>
              <Skeleton className="h-7 w-56" />
              <Skeleton className="h-4 w-72" />
            </>
          ) : detail.isError ? (
            <h1 className="text-2xl font-semibold text-destructive">
              {detail.error instanceof ApiError ? detail.error.message : 'Failed to load.'}
            </h1>
          ) : card ? (
            <>
              <h1 className="text-2xl font-semibold">{card.name}</h1>
              <p className="text-sm text-muted-foreground">
                Effective {card.effectiveFrom}
                {card.effectiveTo ? ` – ${card.effectiveTo}` : ' (open)'}
              </p>
            </>
          ) : (
            <h1 className="text-2xl font-semibold">Not found</h1>
          )}
        </div>
        {card ? (
          <RequirePermission permission="ratecard.edit">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setEditorOpen(true)}>
                <Pencil />
                Edit
              </Button>
              <Button
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={() => setConfirmDelete(true)}
                disabled={deleteMutation.isPending}
              >
                <Trash2 />
                Delete
              </Button>
            </div>
          </RequirePermission>
        ) : null}
      </header>

      <PillTabs
        value={activeTab}
        onValueChange={(v) =>
          navigate({
            search: (s) => ({ ...s, tab: v === DEFAULT_TAB ? undefined : (v as TabValue) }),
            replace: true,
          })
        }
        className="rounded-xl border bg-card p-4"
      >
        <PillTabsList fullWidth>
          <PillTabsTrigger value="details">Details</PillTabsTrigger>
          {canViewAudit ? (
            <PillTabsTrigger value="audit">
              Audit
              {audit.data ? (
                <span className="ml-1 inline-flex items-center rounded-full border border-border bg-background px-2.5 py-0.5 text-xs font-medium text-foreground transition-colors focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-none">
                  {audit.data.meta.total}
                </span>
              ) : null}
            </PillTabsTrigger>
          ) : null}
        </PillTabsList>

        <PillTabsContent value="details" className="space-y-3">
          <div data-surface="table" className="overflow-hidden rounded-md border light:bg-card">
            {detail.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : card ? (
              <LinesTable card={card} />
            ) : (
              <p className="p-4 text-sm text-muted-foreground">Rate card not found.</p>
            )}
          </div>
        </PillTabsContent>

        <PillTabsContent value="audit" className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Every state-changing operation, newest first.
          </p>
          <div className="rounded-md border">
            {audit.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : audit.isError ? (
              <p className="p-4 text-sm text-destructive">
                {audit.error instanceof ApiError
                  ? audit.error.message
                  : 'Failed to load audit log.'}
              </p>
            ) : (
              <AuditEntryList items={audit.data?.data ?? []} total={audit.data?.meta.total ?? 0} />
            )}
          </div>
        </PillTabsContent>
      </PillTabs>

      <RatecardFormSheet
        open={editorOpen}
        onOpenChange={setEditorOpen}
        serviceRoles={serviceRoles}
        current={card}
      />

      <ConfirmDialog
        open={confirmDelete}
        title="Delete rate card?"
        description={
          card
            ? `"${card.name}" will be permanently removed. This cannot be undone.`
            : 'This rate card will be permanently removed.'
        }
        confirmLabel="Delete"
        destructive
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </PageShell>
  );
}

function LinesTable({ card }: { card: Ratecard }) {
  if (card.lines.length === 0) {
    return <p className="p-4 text-sm text-muted-foreground">This card has no lines.</p>;
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-32">Code</TableHead>
          <TableHead>Service role</TableHead>
          <TableHead className="w-40 text-right">Rate</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {card.lines.map((l) => (
          <TableRow key={l.serviceRoleId}>
            <TableCell className="font-mono text-xs">
              {l.serviceRoleShortCode ?? l.serviceRoleId.slice(0, 8)}
            </TableCell>
            <TableCell className="text-sm">{l.serviceRoleName ?? '—'}</TableCell>
            <TableCell className="text-right font-mono text-sm">{l.rate.toFixed(2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
